import { DelegateAdminClaim } from '@eigtech/ui-shared-claims-lib'
import {
  Button,
  getGoogleMapQueryLink,
  Property,
  PropertyList,
  useDisclosure,
} from '@eigtech/ui-shared-dave'
import { isNumber } from 'lodash-es'
import { useEffect } from 'react'
import Map, { Marker, Popup, useMap } from 'react-map-gl'
import { useGetClaim, useGetLatLongForClaim } from '../../../api'
import { mapboxAccessToken } from '../../../lib/constants'

export type ClaimMapProps = {
  claimNumber: string
}

export function ClaimMap({ claimNumber }: ClaimMapProps) {
  const { data: claim } = useGetClaim(claimNumber)

  return (
    <>
      <Map
        attributionControl={false}
        initialViewState={{
          longitude: claim?.lossLocation?.geoCoordinates?.longitude ?? -100,
          latitude: claim?.lossLocation?.geoCoordinates?.latitude ?? 40,
          zoom: 19,
        }}
        mapStyle="mapbox://styles/mapbox/satellite-v9?optimize=true"
        mapboxAccessToken={mapboxAccessToken}
        style={{ width: '100%', height: '60vh' }}
      >
        <Location claimNumber={claimNumber} location={claim?.lossLocation} />
      </Map>
    </>
  )
}

function Location({
  claimNumber,
  location,
}: {
  claimNumber: string
  location: DelegateAdminClaim['lossLocation'] | undefined
}) {
  const mapRef = useMap()

  const { data: latLong } = useGetLatLongForClaim({ claimNumber, location })

  useEffect(() => {
    const { latitude, longitude } = latLong ?? {}

    if (!(latitude && longitude)) return

    mapRef.current?.flyTo({
      center: {
        lat: latitude,
        lon: longitude,
      },
      zoom: 19,
      duration: 500,
    })
  }, [latLong, mapRef])

  const latitude = latLong?.latitude
  const longitude = latLong?.longitude

  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: true })

  return isNumber(latitude) && isNumber(longitude) ? (
    <>
      <Marker
        latitude={latitude}
        longitude={longitude}
        onClick={(e) => {
          // If we let the click event propagates to the map, it will immediately close the popup
          // with `closeOnClick: true`
          e.originalEvent.stopPropagation()
          onOpen()
        }}
      />

      {isOpen && (
        <Popup
          anchor="top"
          focusAfterOpen={false}
          latitude={latitude}
          longitude={longitude}
          onClose={onClose}
        >
          <PropertyList variant="vertical-stacked">
            <Property label="Street 1" value={location?.address.addressLine1} />
            <Property label="Street 2" value={location?.address.addressLine2} />
            <Property label="Street 3" value={location?.address.addressLine3} />
            <Property label="City" value={location?.address.city} />
            <Property label="State" value={location?.address.stateOrProvince} />
            <Property label="Postal Code" value={location?.address.zipOrPostalCode} />
          </PropertyList>

          {!!location?.address && (
            <Button
              as="a"
              href={getGoogleMapQueryLink(location.address)}
              mt="2"
              size="xs"
              target={`${claimNumber}_lossLocation`}
              variant="outline"
              w="full"
            >
              Open in Google Maps
            </Button>
          )}
        </Popup>
      )}
    </>
  ) : null
}
